import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { statsApi } from "@/api/stats";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { stageLabel } from "@/lib/stage";

export interface TeamRef {
  id: string;
  name: string;
  flag: string | null;
}

export function TeamStatsModal({ team, onClose }: { team: TeamRef | null; onClose: () => void }) {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["teamStats", team?.id, scope.leagueId, scope.tournamentId],
    queryFn: () => statsApi.teamStats(team!.id, scope),
    enabled: !!team,
  });

  return (
    <Dialog open={!!team} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {team && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TeamFlag name={team.name} flag={team.flag} className="size-6" />
                {team.name}
              </DialogTitle>
            </DialogHeader>

            <DialogBody className="space-y-4">
              {q.isLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {q.error && <p className="text-sm text-destructive">Nem sikerült betölteni a csapat statisztikáját.</p>}
              {q.data && (
                <>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    <Tile label="Lej." value={q.data.played} />
                    <Tile label="Gy" value={q.data.wins} />
                    <Tile label="D" value={q.data.draws} />
                    <Tile label="V" value={q.data.losses} />
                    <Tile label="Lőtt" value={q.data.goalsFor} />
                    <Tile label="Kapott" value={q.data.goalsAgainst} />
                  </div>

                  {q.data.matches.length > 0 && (
                    <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
                      {q.data.matches.map((m, i) => (
                        <li key={i} className="flex items-center gap-2 px-3 py-2 text-sm">
                          <span className="hidden w-24 shrink-0 text-xs text-muted-foreground sm:inline">{stageLabel(m.stage)}</span>
                          <span className="min-w-0 flex-1 truncate">
                            <TeamFlag name={m.opponent} flag={m.opponentFlag} className="mr-1.5 inline size-4 align-text-bottom" />
                            {m.opponent}
                          </span>
                          <span className="font-semibold tabular-nums">{m.scoreSelf}–{m.scoreOpponent}</span>
                          <span className={cn("w-5 text-center text-xs font-bold", m.result === "W" ? "text-success" : m.result === "L" ? "text-destructive" : "text-muted-foreground")}>
                            {m.result}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </DialogBody>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border/60 bg-background/40 p-2 text-center">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
